import { useMemo } from "react";
import { parseBigFloat } from "@wtc/ui";
import { useCeoForecastMetrics } from "./use-ceo-forecast-metrics";
import { useCeoHealthMetricsGraphQL } from "./use-ceo-health-metrics-graphql";
import { useCeoSalesPerformanceMetrics } from "./use-ceo-sales-performance-metrics";
import { getBucketPreset } from "@/features/deals/hooks/bucket-config";
import { ALL_STAGES } from "@/lib/constants/deal-stages";
import { getStageLabelForDisplay } from "@/lib/utils/deal/deal-lifecycle";
import type { DealStage } from "@/lib/types/database/deals";
import {
  CeoAnalyticsData,
  UseCeoAnalyticsDataOptions,
  StageData,
  SizeData,
  HealthDistributionData,
} from "./ceo-analytics-types";
import {
  transformWinRateTrendData,
  transformACVDistributionData,
  transformConversionFunnelData,
  transformCompanyScatterData,
  transformTeamUtilizationData,
  transformForecastTrendData,
} from "./ceo-analytics-transforms";

// Re-export types so existing imports from this file continue to work
export type {
  StageData,
  SizeData,
  ForecastTrendPoint,
  HealthDistributionData,
  CompanyScatterPoint,
  UtilizationData,
  WinRateTrendData,
  ACVDistributionData,
  ConversionFunnelData,
  CeoAnalyticsData,
} from "./ceo-analytics-types";

// ============================================================================
// CEO ANALYTICS DATA HOOK
// ============================================================================
//
// PURPOSE: Transform CEO dashboard data into chart-ready formats.
//
// All inputs are typed against the codegen GraphQL nodes (HealthCompanyNode /
// HealthUserNode / HealthTimeEntryNode) and the canonical forecast/stage
// helpers — never `any`. A field the query doesn't select is therefore a
// compile error, not a silently-empty chart. The per-chart mapping lives in
// pure functions in ./ceo-analytics-transforms (unit-tested) — this hook only
// wires the source hooks to them.
//
// ============================================================================

/** UTC midnight of "today" — stable anchor for the forecast-trend day buckets. */
function startOfTodayUtc(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

export function useCeoAnalyticsData(
  options: UseCeoAnalyticsDataOptions = {}
): CeoAnalyticsData {
  const { enabled = true, dateFrom, dateTo, bucketPresetId = "default" } = options;

  const bucketPreset = getBucketPreset(bucketPresetId);

  const {
    deals: forecastDeals,
    revenueTarget,
    isLoading: forecastLoading,
    error: forecastError,
  } = useCeoForecastMetrics({ enabled, dateFrom, dateTo });

  const {
    metrics: healthMetrics,
    isLoading: healthLoading,
    error: healthError,
    companies: healthCompanies,
    users: healthUsers,
    timeEntries: healthTimeEntries,
  } = useCeoHealthMetricsGraphQL({ enabled, dateFrom, dateTo });

  const {
    metrics: salesMetrics,
    deals: salesDeals,
    isLoading: salesLoading,
    error: salesError,
  } = useCeoSalesPerformanceMetrics({ enabled, dateFrom, dateTo });

  // Capacity window for utilization: the selected period (defaults to 30 days),
  // matching the window time entries were fetched over.
  const weeksInPeriod = useMemo(() => {
    if (!dateFrom) return 30 / 7;
    const from = new Date(dateFrom).getTime();
    const to = (dateTo ? new Date(dateTo) : new Date()).getTime();
    const days = Math.round((to - from) / (1000 * 60 * 60 * 24));
    return (Number.isFinite(days) && days > 0 ? days : 30) / 7;
  }, [dateFrom, dateTo]);

  const analyticsData = useMemo((): Omit<CeoAnalyticsData, "isLoading" | "error"> => {
    // ========================================================================
    // DEALS BY STAGE — canonical order + display labels
    // ========================================================================
    const stageData: StageData[] = [];
    if (forecastDeals && forecastDeals.length > 0) {
      const stageMap = new Map<DealStage, { count: number; revenue: number }>();
      forecastDeals.forEach((deal) => {
        const existing = stageMap.get(deal.stage) || { count: 0, revenue: 0 };
        stageMap.set(deal.stage, {
          count: existing.count + 1,
          revenue: existing.revenue + (parseBigFloat(deal.revenue) ?? 0),
        });
      });

      Array.from(stageMap.entries())
        .sort((a, b) => {
          const ai = ALL_STAGES.indexOf(a[0]);
          const bi = ALL_STAGES.indexOf(b[0]);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        })
        .forEach(([stage, data]) => {
          stageData.push({
            stage,
            count: data.count,
            revenue: data.revenue,
            displayName: getStageLabelForDisplay(stage),
          });
        });
    }

    // ========================================================================
    // DEALS BY SIZE DISTRIBUTION
    // ========================================================================
    const sizeData: SizeData[] = bucketPreset.buckets.map((bucket) => ({
      bucket: bucket.label,
      minAmount: bucket.minAmount,
      maxAmount: bucket.maxAmount,
      count: 0,
    }));
    if (forecastDeals && forecastDeals.length > 0) {
      forecastDeals.forEach((deal) => {
        const amount = parseBigFloat(deal.revenue) ?? 0;
        const bucket = sizeData.find((b) => amount >= b.minAmount && amount < b.maxAmount);
        if (bucket) bucket.count++;
      });
    }

    // ========================================================================
    // PROJECT HEALTH DISTRIBUTION
    // ========================================================================
    const healthDistributionData: HealthDistributionData[] = [
      { status: "red", count: 0, displayName: "At Risk", color: "#dc2626" },
      { status: "yellow", count: 0, displayName: "Caution", color: "#ea8c55" },
      { status: "green", count: 0, displayName: "Healthy", color: "#16a34a" },
    ];
    if (healthMetrics) {
      healthDistributionData[0].count = healthMetrics.activeProjectsRed || 0;
      healthDistributionData[1].count = healthMetrics.activeProjectsYellow || 0;
      healthDistributionData[2].count = healthMetrics.activeProjectsGreen || 0;
    }

    return {
      stageData,
      sizeData,
      forecastTrendData: transformForecastTrendData(
        forecastDeals || [],
        revenueTarget?.target_amount ?? 0,
        startOfTodayUtc()
      ),
      healthDistributionData,
      companyScatterData: transformCompanyScatterData(healthCompanies || []),
      utilizationData: transformTeamUtilizationData(
        healthUsers || [],
        healthTimeEntries || [],
        weeksInPeriod
      ),
      winRateTrendData: transformWinRateTrendData(salesDeals || []),
      acvDistributionData: transformACVDistributionData(salesDeals || [], bucketPreset),
      conversionFunnelData: transformConversionFunnelData(
        salesDeals || [],
        salesMetrics?.totalCommunications || 0
      ),
    };
  }, [
    forecastDeals,
    revenueTarget,
    healthMetrics,
    healthCompanies,
    healthUsers,
    healthTimeEntries,
    weeksInPeriod,
    salesDeals,
    salesMetrics,
    bucketPreset,
  ]);

  return {
    ...analyticsData,
    isLoading: forecastLoading || healthLoading || salesLoading,
    error: forecastError || healthError || salesError || null,
  };
}
